import type { ChannelClient, ChannelServer } from './channel';

/**
 * Automatic RPC proxy (架构方案 §5.1 / §5.5). Together these two helpers let a
 * service be implemented once on the host side and consumed transparently on the
 * client side: the caller programs against the plain interface `T`, unaware that
 * each method hop crosses a process boundary over a Channel.
 *
 * Wire convention: a method `foo` of service `name` is addressed as `name:foo`.
 */

type AnyFunction = (...args: unknown[]) => unknown;

/**
 * Registers every function-valued property of `impl` as a Channel handler under
 * the `channelName:method` namespace, so a `createServiceProxy` on the peer can
 * invoke them.
 */
export function registerChannelService<T extends object>(
  server: ChannelServer,
  channelName: string,
  impl: T,
): void {
  for (const key of Object.keys(impl)) {
    const value = (impl as Record<string, unknown>)[key];
    if (typeof value === 'function') {
      const fn = value as AnyFunction;
      server.handle(`${channelName}:${key}`, (...args) => fn.apply(impl, args));
    }
  }
}

/**
 * Builds a proxy that forwards every method access to the remote service via
 * `client.call('channelName:method', ...args)`. Returns `Promise`-typed results,
 * matching the request/response Channel surface.
 */
export function createServiceProxy<T extends object>(
  client: ChannelClient,
  channelName: string,
): T {
  return new Proxy({} as T, {
    get(_target, prop) {
      if (typeof prop !== 'string') {
        return undefined;
      }
      // Reserve the Promise-protocol property: the proxy returns a function for
      // every property, which would otherwise make it look like a thenable and
      // trap any `await`/`Promise.resolve(proxy)` into calling `proxy.then(...)`
      // — sending the resolve/reject functions over the Channel (uncloneable).
      // RPC services therefore cannot expose a method literally named `then`.
      if (prop === 'then') {
        return undefined;
      }
      return (...args: unknown[]): Promise<unknown> =>
        client.call(`${channelName}:${prop}`, ...args);
    },
  });
}
